/**
 * Answer marks: the emphasis the globe puts on a country when it has something
 * to SAY about it — "you tapped here and you were right", "you tapped here and
 * you were wrong", "here is the one you missed".
 *
 * WHY THIS IS NOT JUST `highlight()`. A highlight is a feature-state colour
 * swap on the country's own geometry, and for a country the size of Brazil it
 * is plenty. But 30 pool members have NO polygon at all — they render as a
 * 2.6px dot (`microstateDots`) — and another sixty-odd are a two-to-five-pixel
 * smudge at the default view. Recolouring five pixels green is feedback the
 * player cannot see, and it is missing at exactly the place the pinpoint was
 * hardest. A mark is a screen-space ring anchored to the country, so it is the
 * same size whether it lands on Russia or on Nauru.
 *
 * Everything here is pure so it can be tested without a GPU — see the header of
 * `projection.ts` for why that matters in this package.
 */

import type { Feature, FeatureCollection, Point } from 'geojson';

import type { LngLatTuple } from './geo';
import { pickTarget } from './targets';

/** What a mark means. Same two words the highlight feature-state uses. */
export type MarkKind = 'correct' | 'incorrect';

export interface MarkRecord {
  readonly id: string;
  readonly cca3: string;
  readonly lngLat: LngLatTuple;
  readonly kind: MarkKind;
}

export interface MarkProps {
  readonly mid: string;
  readonly kind: MarkKind;
}

export type MarkCollection = FeatureCollection<Point, MarkProps>;

/**
 * Where a country's mark sits.
 *
 * `pickTarget` is the right index and not an accident: it is built from the
 * same anchors the SNAP resolver aims at, so the ring lands exactly where the
 * player was being asked to tap — including for the countries with no geometry,
 * whose anchor is their curated centre rather than a polygon centroid.
 */
export function markAnchor(cca3: string): LngLatTuple | null {
  const target = pickTarget(cca3);
  return target ? target.anchor : null;
}

export function emptyMarkCollection(): MarkCollection {
  return { type: 'FeatureCollection', features: [] };
}

export function markCollection(marks: Iterable<MarkRecord>): MarkCollection {
  const features: Array<Feature<Point, MarkProps>> = [];
  for (const m of marks) {
    features.push({
      type: 'Feature',
      id: m.id,
      properties: { mid: m.id, kind: m.kind },
      geometry: { type: 'Point', coordinates: [m.lngLat[0], m.lngLat[1]] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** One full pulse, in milliseconds. Slow enough to read, fast enough to notice. */
export const PULSE_PERIOD_MS = 1_100;
/** Ring radius the pulse starts at, in CSS pixels. */
export const PULSE_MIN_PX = 10;
/** Ring radius the pulse dies at. Comfortably wider than a fingertip. */
export const PULSE_MAX_PX = 46;

/** Position within the current pulse, 0 at the birth of the ring, 1 at its death. */
export function pulsePhase(elapsedMs: number, periodMs: number = PULSE_PERIOD_MS): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  if (periodMs <= 0) return 0;
  return (elapsedMs % periodMs) / periodMs;
}

/**
 * Radius for a phase. Eased out so the ring leaps away from the country and
 * then drifts, which reads as an expanding shockwave rather than a metronome.
 */
export function pulseRadiusPx(
  phase: number,
  minPx: number = PULSE_MIN_PX,
  maxPx: number = PULSE_MAX_PX,
): number {
  const p = Math.min(1, Math.max(0, phase));
  const eased = 1 - (1 - p) * (1 - p);
  return minPx + (maxPx - minPx) * eased;
}

/** Opacity for a phase: bright at birth, gone by death. */
export function pulseOpacity(phase: number, peak = 0.45): number {
  const p = Math.min(1, Math.max(0, phase));
  // Fade in over the first tenth so the ring does not pop into existence.
  const rise = Math.min(1, p / 0.1);
  return peak * rise * (1 - p);
}
